import React, { createContext, useContext, useMemo, useState, useRef, useCallback } from "react";
import { RadioContext } from "./RadioProvider";

import useAudioElement from "./audio/useAudioElement";
import useAudioTime from "./audio/useAudioTime";
import useProgress from "./audio/useProgress";
import useVisualizer from "./audio/useVisualizer";
import useManualTrack from "./audio/useManualTrack";

export const AudioContext = createContext(null);

export const AudioProvider = ({ children }) => {
  const { nowPlaying } = useContext(RadioContext);

  const [timeShiftOffsetSec, setTimeShiftOffsetSec] = useState(0);
  const [fragProgramDateMs, setFragProgramDateMs] = useState(null);
  const [fragStartSec, setFragStartSec] = useState(0);
  const [playbackState, setPlaybackState] = useState("stopped");

  const pausedTimeRef = useRef(null);
  const audioRef = useRef(null);
  const currentTimeRef = useRef(0);
  const audioContextRef = useRef(null);
  const analyserRef = useRef(null);
  const rafIdRef = useRef(null);

  const {
    audioTimeMs,
    playbackTimeMs,
    bufferStartMs,
    currentLiveMs,
  } = useAudioTime(
    audioRef,
    playbackState,
    nowPlaying,
    timeShiftOffsetSec,
    fragProgramDateMs,
    currentTimeRef,
    fragStartSec,
    pausedTimeRef,
  );

  const {
    attachHls,
    detachHls,
  } = useAudioElement(
    setTimeShiftOffsetSec,
    timeShiftOffsetSec,
    setFragProgramDateMs,
    setFragStartSec,
    nowPlaying,
    playbackTimeMs,
    audioTimeMs,
    pausedTimeRef,
    audioRef,
    playbackState,
    setPlaybackState,
  );

  const { progressBarPercent } = useProgress(playbackTimeMs, bufferStartMs, currentLiveMs);

  const { dataArrayRef } = useVisualizer(audioRef, playbackState, audioContextRef, analyserRef, rafIdRef);

  const { manualTrack, setManualTrack, isSeeking, setIsSeeking } = useManualTrack(
    nowPlaying,
    audioTimeMs
  );

  const setCurrentTimeSec = useCallback((value) => {
    currentTimeRef.current = value;
  }, []);

  const contextValue = useMemo(
    () => ({
      audioRef,
      playbackState,
      pausedTimeRef,
      setPlaybackState,
      currentTimeRef,
      currentLiveMs,
      bufferStartMs,
      audioTimeMs,
      attachHls,
      detachHls,
      setCurrentTimeSec,
      manualTrack,
      setManualTrack,
      isSeeking,
      setIsSeeking,
      playbackTimeMs,
      progressBarPercent,
      timeShiftOffsetSec,
      setTimeShiftOffsetSec,
      dataArrayRef,
    }),
    [
      playbackState,
      currentTimeRef,
      currentLiveMs,
      bufferStartMs,
      audioTimeMs,
      manualTrack,
      isSeeking,
      playbackTimeMs,
      progressBarPercent,
      timeShiftOffsetSec,
      attachHls,
      setCurrentTimeSec,
    ]
  );

  return (
    <AudioContext.Provider value={contextValue}>
      <audio ref={audioRef} preload="auto" style={{ display: "none" }} />
      {children}
    </AudioContext.Provider>
  );
};
